import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    VStack,
    HStack,
    Card,
    CardHeader,
    CardBody,
    Heading,
    Text,
    useToast,
    Alert,
    AlertIcon,
    Code,
    Badge,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
    Input,
    Select,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    useDisclosure,
    IconButton,
    Tooltip,
    FormControl,
    FormLabel,
    Flex,
    Spacer,
    SimpleGrid,
    Image,
} from '@chakra-ui/react';
import { FiPlus, FiTrash2, FiRefreshCw, FiEdit3 } from 'react-icons/fi';
import { canvasAPI } from '../../../services/api';

const GlobalResourcesTab = ({ settings, loading, onRefresh }) => {
    const [customVariables, setCustomVariables] = useState({});
    const [availableImages, setAvailableImages] = useState([]);
    const [isUploadingImage, setIsUploadingImage] = useState(false);
    const [pendingImageFile, setPendingImageFile] = useState(null);
    const [suggestedImageName, setSuggestedImageName] = useState('');
    const [showNamePrompt, setShowNamePrompt] = useState(false);
    const [newVariable, setNewVariable] = useState({
        name: '',
        value: '',
        type: 'global',
        description: '',
        example: ''
    });
    const [editingVariableName, setEditingVariableName] = useState(null);

    const { isOpen, onOpen, onClose } = useDisclosure();
    const { isOpen: isImageModalOpen, onOpen: onImageModalOpen, onClose: onImageModalClose } = useDisclosure();
    const toast = useToast();

    useEffect(() => {
        if (settings?.custom_variables) {
            setCustomVariables(settings.custom_variables);
        }
        loadAvailableImages();
    }, [settings]);

    const loadAvailableImages = async () => {
        try {
            const response = await canvasAPI.getAvailableImages();
            setAvailableImages(response.images || []);
        } catch (error) {
            console.error('Error loading images:', error);
        }
    };

    const handleAddVariable = async () => {
        try {
            await canvasAPI.addCustomVariable(newVariable);

            toast({
                title: 'Variable agregada',
                description: `La variable {{${newVariable.name}}} se ha agregado correctamente`,
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

            setNewVariable({
                name: '',
                value: '',
                type: 'global',
                description: '',
                example: ''
            });

            onClose();
            if (onRefresh) await onRefresh();
        } catch (error) {
            console.error('Error adding variable:', error);
            toast({
                title: 'Error',
                description: error.response?.data?.detail || 'No se pudo agregar la variable',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const handleEditVariable = (variableName) => {
        const variableData = customVariables[variableName];
        setNewVariable({
            name: variableName,
            value: variableData.value || '',
            type: variableData.type,
            description: variableData.description || '',
            example: variableData.example || ''
        });
        setEditingVariableName(variableName);
        onOpen();
    };

    const handleUpdateVariable = async () => {
        try {
            await canvasAPI.updateCustomVariable(editingVariableName, newVariable);

            toast({
                title: 'Variable actualizada',
                description: `La variable {{${newVariable.name}}} se ha actualizado correctamente`,
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

            setNewVariable({
                name: '',
                value: '',
                type: 'global',
                description: '',
                example: ''
            });
            setEditingVariableName(null);

            onClose();
            if (onRefresh) await onRefresh();
        } catch (error) {
            console.error('Error updating variable:', error);
            toast({
                title: 'Error',
                description: error.response?.data?.detail || 'No se pudo actualizar la variable',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const handleDeleteVariable = async (variableName) => {
        try {
            await canvasAPI.deleteCustomVariable(variableName);

            toast({
                title: 'Variable eliminada',
                description: `La variable {{${variableName}}} se ha eliminado`,
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

            if (onRefresh) await onRefresh();
        } catch (error) {
            console.error('Error deleting variable:', error);
            toast({
                title: 'Error',
                description: 'No se pudo eliminar la variable',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const handleUploadImage = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const fileNameWithoutExt = file.name.substring(0, file.name.lastIndexOf('.'));
        const fileExt = file.name.substring(file.name.lastIndexOf('.'));

        if (fileNameWithoutExt.length > 20) {
            const shortName = fileNameWithoutExt.substring(0, 15).replace(/[^a-zA-Z0-9_-]/g, '_');
            const timestamp = Date.now().toString().slice(-4);
            const suggestedName = `${shortName}_${timestamp}${fileExt}`;

            setPendingImageFile(file);
            setSuggestedImageName(suggestedName);
            setShowNamePrompt(true);
            event.target.value = '';
            return;
        }

        await uploadImageFile(file);
        event.target.value = '';
    };

    const uploadImageFile = async (file, newName = null) => {
        try {
            setIsUploadingImage(true);

            let fileToUpload = file;
            if (newName) {
                fileToUpload = new File([file], newName, { type: file.type });
            }

            await canvasAPI.uploadCustomImage(fileToUpload);

            toast({
                title: 'Imagen subida',
                description: `La imagen ${fileToUpload.name} se ha subido correctamente`,
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

            await loadAvailableImages();
        } catch (error) {
            console.error('Error uploading image:', error);
            toast({
                title: 'Error',
                description: error.response?.data?.detail || 'No se pudo subir la imagen',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsUploadingImage(false);
            setPendingImageFile(null);
            setSuggestedImageName('');
            setShowNamePrompt(false);
        }
    };

    const handleConfirmImageName = async () => {
        if (pendingImageFile) {
            await uploadImageFile(pendingImageFile, suggestedImageName);
        }
    };

    const handleKeepOriginalName = async () => {
        if (pendingImageFile) {
            await uploadImageFile(pendingImageFile);
        }
    };

    const handleDeleteImage = async (filename) => {
        try {
            await canvasAPI.deleteCustomImage(filename);

            toast({
                title: 'Imagen eliminada',
                description: `La imagen ${filename} se ha eliminado`,
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

            await loadAvailableImages();
        } catch (error) {
            console.error('Error deleting image:', error);
            toast({
                title: 'Error',
                description: error.response?.data?.detail || 'No se pudo eliminar la imagen',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const globalVariables = [
        { name: 'director_nombre', value: settings?.director_name || 'No configurado', description: 'Nombre del director de la institución' },
        { name: 'director_cargo', value: settings?.director_title || 'No configurado', description: 'Cargo del director' },
        { name: 'institucion_nombre', value: settings?.institution_name || 'No configurado', description: 'Nombre de la institución' },
        { name: 'institution_color', value: settings?.institution_primary_color || '#202D5C', description: 'Color primario institucional (sin #)' },
    ];

    const courseVariables = [
        { name: 'nombre_estudiante', example: 'Juan Pérez García', description: 'Nombre completo del estudiante' },
        { name: 'nombre_curso', example: 'Desarrollo Web con Python', description: 'Nombre del curso Canvas' },
        { name: 'duracion', example: '40', description: 'Duración del curso en horas' },
        { name: 'fecha_emision', example: '15/12/2024', description: 'Fecha de emisión del certificado (DD/MM/YYYY)' },
        { name: 'id_certificado', example: 'CERT-20241215-ABC123-XYZ', description: 'ID único del certificado' },
        { name: 'profesor_nombre', example: 'María González', description: 'Nombre del instructor que emite el certificado' },
        { name: 'profesor_cargo', example: 'Profesor del Curso', description: 'Cargo del profesor (fijo)' },
    ];

    return (
        <Box>
            <Card mb={6}>
                <CardHeader>
                    <Flex align="center">
                        <Box flex="1">
                            <Heading size="md">Variables e Imágenes Globales</Heading>
                            <Text fontSize="sm" color="gray.600" mt={1}>
                                Recursos compartidos disponibles para todas las plantillas de certificados
                            </Text>
                        </Box>
                        <Button
                            leftIcon={<FiRefreshCw />}
                            onClick={onRefresh}
                            variant="ghost"
                            size="sm"
                        >
                            Recargar
                        </Button>
                    </Flex>
                </CardHeader>
            </Card>

            <VStack spacing={6} align="stretch">
                <Card>
                    <CardHeader pb={2}>
                        <Heading size="sm">Variables Disponibles</Heading>
                        <Text fontSize="xs" color="gray.600" mt={1}>
                            Usa estas variables en tus plantillas LaTeX con la sintaxis: <Code colorScheme="blue">{'{{variable}}'}</Code>
                        </Text>
                    </CardHeader>
                    <CardBody>
                        <Accordion allowMultiple defaultIndex={[0, 1, 2]}>
                            <AccordionItem>
                                <AccordionButton>
                                    <Box flex="1" textAlign="left">
                                        <Text fontWeight="bold" fontSize="sm">
                                            Variables Globales <Badge ml={2} colorScheme="green">Sistema</Badge>
                                        </Text>
                                    </Box>
                                    <AccordionIcon />
                                </AccordionButton>
                                <AccordionPanel pb={4}>
                                    <VStack align="stretch" spacing={3}>
                                        {globalVariables.map((variable) => (
                                            <Box key={variable.name} p={2} bg="gray.50" borderRadius="md">
                                                <HStack justify="space-between">
                                                    <Code colorScheme="blue" fontSize="xs">
                                                        {'{{' + variable.name + '}}'}
                                                    </Code>
                                                </HStack>
                                                <Text fontSize="xs" color="gray.600" mt={1}>
                                                    {variable.description}
                                                </Text>
                                                <Text fontSize="xs" fontWeight="bold" mt={1} color="green.600">
                                                    Valor actual: {variable.value}
                                                </Text>
                                            </Box>
                                        ))}
                                    </VStack>
                                </AccordionPanel>
                            </AccordionItem>

                            <AccordionItem>
                                <AccordionButton>
                                    <Box flex="1" textAlign="left">
                                        <Text fontWeight="bold" fontSize="sm">
                                            Variables por Curso <Badge ml={2} colorScheme="orange">Dinámicas</Badge>
                                        </Text>
                                    </Box>
                                    <AccordionIcon />
                                </AccordionButton>
                                <AccordionPanel pb={4}>
                                    <VStack align="stretch" spacing={3}>
                                        {courseVariables.map((variable) => (
                                            <Box key={variable.name} p={2} bg="orange.50" borderRadius="md">
                                                <Code colorScheme="blue" fontSize="xs">
                                                    {'{{' + variable.name + '}}'}
                                                </Code>
                                                <Text fontSize="xs" color="gray.600" mt={1}>
                                                    {variable.description}
                                                </Text>
                                                <Text fontSize="xs" fontStyle="italic" mt={1} color="orange.600">
                                                    Ejemplo: {variable.example}
                                                </Text>
                                            </Box>
                                        ))}
                                    </VStack>
                                </AccordionPanel>
                            </AccordionItem>

                            <AccordionItem>
                                <AccordionButton>
                                    <Box flex="1" textAlign="left">
                                        <Text fontWeight="bold" fontSize="sm">
                                            Variables Personalizadas <Badge ml={2} colorScheme="purple">Custom</Badge>
                                        </Text>
                                    </Box>
                                    <AccordionIcon />
                                </AccordionButton>
                                <AccordionPanel pb={4}>
                                    <VStack align="stretch" spacing={3}>
                                        {Object.keys(customVariables).length === 0 ? (
                                            <Text fontSize="xs" color="gray.500" textAlign="center">
                                                No hay variables personalizadas
                                            </Text>
                                        ) : (
                                            Object.entries(customVariables).map(([name, data]) => (
                                                <Box
                                                    key={name}
                                                    p={2}
                                                    bg={data.type === 'global' ? 'purple.50' : 'pink.50'}
                                                    borderRadius="md"
                                                >
                                                    <HStack justify="space-between">
                                                        <Code colorScheme="blue" fontSize="xs">
                                                            {'{{' + name + '}}'}
                                                        </Code>
                                                        <HStack spacing={1}>
                                                            <IconButton
                                                                icon={<FiEdit3 />}
                                                                size="xs"
                                                                colorScheme="blue"
                                                                variant="ghost"
                                                                onClick={() => handleEditVariable(name)}
                                                                aria-label="Editar variable"
                                                            />
                                                            <IconButton
                                                                icon={<FiTrash2 />}
                                                                size="xs"
                                                                colorScheme="red"
                                                                variant="ghost"
                                                                onClick={() => handleDeleteVariable(name)}
                                                            />
                                                        </HStack>
                                                    </HStack>
                                                    <Badge colorScheme={data.type === 'global' ? 'green' : 'orange'} fontSize="2xs" mt={1}>
                                                        {data.type === 'global' ? 'Global' : 'Por Curso'}
                                                    </Badge>
                                                    {data.description && (
                                                        <Text fontSize="xs" color="gray.600" mt={1}>
                                                            {data.description}
                                                        </Text>
                                                    )}
                                                    {data.type === 'global' && data.value && (
                                                        <Text fontSize="xs" fontWeight="bold" mt={1} color="purple.600">
                                                            Valor: {data.value}
                                                        </Text>
                                                    )}
                                                    {data.type === 'course' && data.example && (
                                                        <Text fontSize="xs" fontStyle="italic" mt={1} color="pink.600">
                                                            Ejemplo: {data.example}
                                                        </Text>
                                                    )}
                                                </Box>
                                            ))
                                        )}
                                        <Button
                                            leftIcon={<FiPlus />}
                                            size="sm"
                                            colorScheme="purple"
                                            variant="outline"
                                            onClick={onOpen}
                                            w="full"
                                        >
                                            Nueva Variable
                                        </Button>
                                    </VStack>
                                </AccordionPanel>
                            </AccordionItem>
                        </Accordion>
                    </CardBody>
                </Card>

                <Card>
                    <CardHeader pb={2}>
                        <HStack justify="space-between">
                            <Heading size="sm">Imágenes Disponibles</Heading>
                            <Tooltip label="Agregar imagen">
                                <IconButton
                                    icon={<FiPlus />}
                                    size="xs"
                                    colorScheme="blue"
                                    onClick={onImageModalOpen}
                                />
                            </Tooltip>
                        </HStack>
                        <Text fontSize="xs" color="gray.600" mt={1}>
                            Imágenes compartidas disponibles para usar en cualquier plantilla LaTeX
                        </Text>
                    </CardHeader>
                    <CardBody>
                        <Alert status="info" fontSize="xs" py={2} mb={3}>
                            <AlertIcon />
                            <Box>
                                <Text fontWeight="bold">Cómo usar imágenes en LaTeX:</Text>
                                <Code fontSize="2xs" mt={1} display="block">
                                    {'\\includegraphics[height=3cm]{nombre_imagen.png}'}
                                </Code>
                            </Box>
                        </Alert>

                        <Accordion allowMultiple defaultIndex={[0, 1, 2, 3]}>
                            <AccordionItem>
                                <AccordionButton>
                                    <Box flex="1" textAlign="left">
                                        <Text fontWeight="bold" fontSize="sm">
                                            Imágenes configurables <Badge ml={2} colorScheme="green">Sistema</Badge>
                                        </Text>
                                    </Box>
                                    <AccordionIcon />
                                </AccordionButton>
                                <AccordionPanel pb={4}>
                                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                                        {availableImages.filter(img => img.type === 'system').map((image) => (
                                            <Box key={image.filename} p={2} bg="green.50" borderRadius="md">
                                                <HStack spacing={3}>
                                                    <Box
                                                        w="60px"
                                                        h="60px"
                                                        borderRadius="md"
                                                        overflow="hidden"
                                                        border="1px solid"
                                                        borderColor="gray.200"
                                                        bg="white"
                                                        display="flex"
                                                        alignItems="center"
                                                        justifyContent="center"
                                                        flexShrink={0}
                                                    >
                                                        {image.exists ? (
                                                            <Image
                                                                src={canvasAPI.getImageThumbnailUrl(image.filename)}
                                                                alt={image.filename}
                                                                maxW="100%"
                                                                maxH="100%"
                                                                objectFit="contain"
                                                            />
                                                        ) : (
                                                            <Text fontSize="2xs" color="gray.400">No existe</Text>
                                                        )}
                                                    </Box>
                                                    <Box flex="1">
                                                        <Code fontSize="2xs" colorScheme="blue">
                                                            {image.filename}
                                                        </Code>
                                                        <Text fontSize="2xs" color="gray.600" mt={1}>
                                                            {image.description}
                                                        </Text>
                                                        {!image.exists && (
                                                            <Badge colorScheme="red" fontSize="2xs" mt={1}>No configurado</Badge>
                                                        )}
                                                    </Box>
                                                </HStack>
                                            </Box>
                                        ))}
                                    </SimpleGrid>
                                </AccordionPanel>
                            </AccordionItem>

                            <AccordionItem>
                                <AccordionButton>
                                    <Box flex="1" textAlign="left">
                                        <Text fontWeight="bold" fontSize="sm">
                                            Firma del instructor <Badge ml={2} colorScheme="blue">Usuario</Badge>
                                        </Text>
                                    </Box>
                                    <AccordionIcon />
                                </AccordionButton>
                                <AccordionPanel pb={4}>
                                    <Box p={2} bg="blue.50" borderRadius="md">
                                        <Code fontSize="2xs" colorScheme="blue">
                                            Firma_Instructor.png
                                        </Code>
                                        <Text fontSize="2xs" color="gray.600" mt={1}>
                                            Se define por usuario con rol de profesor
                                        </Text>
                                        <Text fontSize="2xs" color="blue.600" mt={1} fontStyle="italic">
                                            �� Acceso: Desde cualquier curso &gt; Subir firma
                                        </Text>
                                    </Box>
                                </AccordionPanel>
                            </AccordionItem>

                            {availableImages.filter(img => img.type === 'decorative').length > 0 && (
                                <AccordionItem>
                                    <AccordionButton>
                                        <Box flex="1" textAlign="left">
                                            <Text fontWeight="bold" fontSize="sm">
                                                Incluidas por defecto <Badge ml={2} colorScheme="orange">Decorativas</Badge>
                                            </Text>
                                        </Box>
                                        <AccordionIcon />
                                    </AccordionButton>
                                    <AccordionPanel pb={4}>
                                        <SimpleGrid columns={{ base: 2, md: 3, lg: 4 }} spacing={3}>
                                            {availableImages.filter(img => img.type === 'decorative').map((image) => (
                                                <Box key={image.filename} p={2} bg="orange.50" borderRadius="md">
                                                    <VStack spacing={2}>
                                                        <Box
                                                            w="100%"
                                                            h="80px"
                                                            borderRadius="md"
                                                            overflow="hidden"
                                                            border="1px solid"
                                                            borderColor="gray.200"
                                                            bg="white"
                                                            display="flex"
                                                            alignItems="center"
                                                            justifyContent="center"
                                                        >
                                                            <Image
                                                                src={canvasAPI.getImageThumbnailUrl(image.filename)}
                                                                alt={image.filename}
                                                                maxW="100%"
                                                                maxH="100%"
                                                                objectFit="contain"
                                                            />
                                                        </Box>
                                                        <Code fontSize="2xs" colorScheme="blue" textAlign="center" noOfLines={2}>
                                                            {image.filename}
                                                        </Code>
                                                        <Text fontSize="2xs" color="gray.500">
                                                            {(image.size / 1024).toFixed(1)} KB
                                                        </Text>
                                                        {image.deletable && (
                                                            <IconButton
                                                                icon={<FiTrash2 />}
                                                                size="xs"
                                                                colorScheme="red"
                                                                variant="ghost"
                                                                onClick={() => handleDeleteImage(image.filename)}
                                                                w="full"
                                                            />
                                                        )}
                                                    </VStack>
                                                </Box>
                                            ))}
                                        </SimpleGrid>
                                    </AccordionPanel>
                                </AccordionItem>
                            )}

                            {availableImages.filter(img => img.type === 'custom').length > 0 && (
                                <AccordionItem>
                                    <AccordionButton>
                                        <Box flex="1" textAlign="left">
                                            <Text fontWeight="bold" fontSize="sm">
                                                Imágenes adicionales <Badge ml={2} colorScheme="purple">Personalizadas</Badge>
                                            </Text>
                                        </Box>
                                        <AccordionIcon />
                                    </AccordionButton>
                                    <AccordionPanel pb={4}>
                                        <SimpleGrid columns={{ base: 2, md: 3, lg: 4 }} spacing={3}>
                                            {availableImages.filter(img => img.type === 'custom').map((image) => (
                                                <Box key={image.filename} p={2} bg="purple.50" borderRadius="md">
                                                    <VStack spacing={2}>
                                                        <Box
                                                            w="100%"
                                                            h="80px"
                                                            borderRadius="md"
                                                            overflow="hidden"
                                                            border="1px solid"
                                                            borderColor="gray.200"
                                                            bg="white"
                                                            display="flex"
                                                            alignItems="center"
                                                            justifyContent="center"
                                                        >
                                                            <Image
                                                                src={canvasAPI.getImageThumbnailUrl(image.filename)}
                                                                alt={image.filename}
                                                                maxW="100%"
                                                                maxH="100%"
                                                                objectFit="contain"
                                                            />
                                                        </Box>
                                                        <Code fontSize="2xs" colorScheme="blue" textAlign="center" noOfLines={2}>
                                                            {image.filename}
                                                        </Code>
                                                        <Text fontSize="2xs" color="gray.500">
                                                            {(image.size / 1024).toFixed(1)} KB
                                                        </Text>
                                                        <IconButton
                                                            icon={<FiTrash2 />}
                                                            size="xs"
                                                            colorScheme="red"
                                                            variant="ghost"
                                                            onClick={() => handleDeleteImage(image.filename)}
                                                            w="full"
                                                        />
                                                    </VStack>
                                                </Box>
                                            ))}
                                        </SimpleGrid>
                                    </AccordionPanel>
                                </AccordionItem>
                            )}
                        </Accordion>
                    </CardBody>
                </Card>
            </VStack>

            <Modal isOpen={isOpen} onClose={() => { onClose(); setEditingVariableName(null); setNewVariable({ name: '', value: '', type: 'global', description: '', example: '' }); }} size="lg">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>{editingVariableName ? 'Editar Variable Personalizada' : 'Agregar Variable Personalizada'}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack spacing={4}>
                            <FormControl isRequired>
                                <FormLabel fontSize="sm">Nombre de la variable</FormLabel>
                                <Input
                                    placeholder="ej: universidad_ciudad"
                                    value={newVariable.name}
                                    onChange={(e) => setNewVariable({ ...newVariable, name: e.target.value })}
                                    fontFamily="monospace"
                                />
                                <Text fontSize="xs" color="gray.500" mt={1}>
                                    Solo letras, números y guiones bajos. Sin espacios ni caracteres especiales.
                                </Text>
                            </FormControl>

                            <FormControl isRequired>
                                <FormLabel fontSize="sm">Tipo de variable</FormLabel>
                                <Select
                                    value={newVariable.type}
                                    onChange={(e) => setNewVariable({ ...newVariable, type: e.target.value })}
                                >
                                    <option value="global">Global (mismo valor para todos los certificados)</option>
                                    <option value="course">Por Curso (se define al crear el certificado)</option>
                                </Select>
                            </FormControl>

                            {newVariable.type === 'global' && (
                                <FormControl isRequired>
                                    <FormLabel fontSize="sm">Valor</FormLabel>
                                    <Input
                                        placeholder="ej: Santiago, Chile"
                                        value={newVariable.value}
                                        onChange={(e) => setNewVariable({ ...newVariable, value: e.target.value })}
                                    />
                                </FormControl>
                            )}

                            <FormControl>
                                <FormLabel fontSize="sm">Descripción</FormLabel>
                                <Input
                                    placeholder="ej: Ciudad donde está ubicada la universidad"
                                    value={newVariable.description}
                                    onChange={(e) => setNewVariable({ ...newVariable, description: e.target.value })}
                                />
                            </FormControl>

                            {newVariable.type === 'course' && (
                                <FormControl>
                                    <FormLabel fontSize="sm">Ejemplo</FormLabel>
                                    <Input
                                        placeholder="ej: 2024-2"
                                        value={newVariable.example}
                                        onChange={(e) => setNewVariable({ ...newVariable, example: e.target.value })}
                                    />
                                </FormControl>
                            )}

                            <Alert status="info" fontSize="xs">
                                <AlertIcon />
                                <Box>
                                    La variable se usará en el template como: <Code colorScheme="blue">{'{{' + (newVariable.name || 'nombre_variable') + '}}'}</Code>
                                </Box>
                            </Alert>
                        </VStack>
                    </ModalBody>

                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={onClose}>
                            Cancelar
                        </Button>
                        <Button
                            colorScheme="purple"
                            onClick={editingVariableName ? handleUpdateVariable : handleAddVariable}
                            isDisabled={!newVariable.name || (newVariable.type === 'global' && !newVariable.value)}
                        >
                            {editingVariableName ? 'Actualizar Variable' : 'Agregar Variable'}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>


            <Modal isOpen={isImageModalOpen} onClose={onImageModalClose} size="md">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Subir Imagen Personalizada</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack spacing={4}>
                            <Alert status="info" fontSize="sm">
                                <AlertIcon />
                                <Box>
                                    <Text fontSize="xs" fontWeight="bold">Formato de imagen aceptado:</Text>
                                    <Text fontSize="xs">PNG, JPG, JPEG, GIF</Text>
                                </Box>
                            </Alert>

                            <FormControl>
                                <FormLabel fontSize="sm">Seleccionar imagen</FormLabel>
                                <Input
                                    type="file"
                                    accept=".png,.jpg,.jpeg,.gif"
                                    onChange={handleUploadImage}
                                    disabled={isUploadingImage}
                                    p={1}
                                />
                                <Text fontSize="xs" color="gray.500" mt={2}>
                                    La imagen estará disponible inmediatamente para usar en tus plantillas LaTeX
                                </Text>
                            </FormControl>

                            <Alert status="warning" fontSize="xs">
                                <AlertIcon />
                                <Box>
                                    <Text fontSize="xs">
                                        No uses nombres reservados del sistema: Firma_Director.png,
                                        Firma_Instructor.png, institution_logo.png
                                    </Text>
                                </Box>
                            </Alert>

                            <Box w="full" p={3} bg="gray.50" borderRadius="md">
                                <Text fontSize="xs" fontWeight="bold" mb={2}>Cómo usar la imagen en LaTeX:</Text>
                                <Code fontSize="2xs" display="block" whiteSpace="pre-wrap">
                                    {'% Ejemplo básico:\n\\includegraphics{tu_imagen.png}\n\n% Con tamaño específico:\n\\includegraphics[width=5cm]{tu_imagen.png}\n\\includegraphics[height=3cm]{tu_imagen.png}\n\n% Desde carpeta static:\n\\includegraphics{static/tu_imagen.png}'}
                                </Code>
                            </Box>
                        </VStack>
                    </ModalBody>

                    <ModalFooter>
                        <Button variant="ghost" onClick={onImageModalClose}>
                            Cerrar
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <Modal isOpen={showNamePrompt} onClose={() => setShowNamePrompt(false)} size="md">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Nombre de archivo muy largo</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack spacing={4} align="stretch">
                            <Alert status="warning">
                                <AlertIcon />
                                <Box>
                                    <Text fontSize="sm" fontWeight="bold">
                                        El nombre del archivo es demasiado largo
                                    </Text>
                                    <Text fontSize="xs" mt={1}>
                                        Los nombres largos pueden dificultar la visualización en la interfaz.
                                    </Text>
                                </Box>
                            </Alert>

                            {pendingImageFile && (
                                <Box>
                                    <Text fontSize="sm" fontWeight="bold" mb={2}>Nombre original:</Text>
                                    <Code fontSize="xs" p={2} display="block" wordBreak="break-all">
                                        {pendingImageFile.name}
                                    </Code>
                                </Box>
                            )}

                            <Box>
                                <Text fontSize="sm" fontWeight="bold" mb={2}>Nombre sugerido:</Text>
                                <Input
                                    value={suggestedImageName}
                                    onChange={(e) => setSuggestedImageName(e.target.value)}
                                    fontSize="sm"
                                />
                                <Text fontSize="xs" color="gray.500" mt={1}>
                                    Puedes modificar el nombre sugerido antes de subir
                                </Text>
                            </Box>
                        </VStack>
                    </ModalBody>

                    <ModalFooter>
                        <HStack spacing={2}>
                            <Button
                                variant="ghost"
                                onClick={handleKeepOriginalName}
                                isLoading={isUploadingImage}
                                size="sm"
                            >
                                Mantener original
                            </Button>
                            <Button
                                colorScheme="blue"
                                onClick={handleConfirmImageName}
                                isLoading={isUploadingImage}
                                size="sm"
                            >
                                Usar nombre sugerido
                            </Button>
                        </HStack>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box >
    );
};

export default GlobalResourcesTab;
